(() => {
  const productSelector = 'product-info[data-zyvoria-finish-gallery]';
  const finishNames = new Set(['finish', 'color', 'colour', 'metal']);

  const normalize = (value) => String(value || '')
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '');

  const canonicalFinish = (value) => {
    const finish = normalize(value);
    if (finish.includes('rose')) return 'rose-gold';
    if (finish.includes('silver')) return 'silver';
    if (finish.includes('gold')) return 'gold';
    return finish;
  };

  const optionName = (input) => normalize(
    input?.name?.replace(/-\d+$/, '').replace(/^options\[/, '').replace(/\].*$/, ''),
  );

  const visibleMedia = (gallery) => [
    ...gallery.querySelectorAll('[data-image-id]:not([hidden])'),
  ];

  // Zyvora adaptive gallery layout -- start
  const updateGalleryLayout = (gallery, count) => {
    const mediaCount = Math.max(0, Number(count) || 0);
    gallery.dataset.zyvoriaVisibleMedia = String(mediaCount);
    gallery.dataset.zyvoriaGalleryLayout = mediaCount <= 1
      ? 'single'
      : mediaCount === 2
        ? 'stack'
        : 'collage';
  };
  // Zyvora adaptive gallery layout -- end

  const patchProductImages = () => {
    const ProductImages = customElements.get('product-images');
    const prototype = ProductImages?.prototype;
    if (!prototype || prototype.zyvoriaFinishNavigation) return;

    prototype.getNextMediaId = function getNextVisibleMediaId() {
      const media = visibleMedia(this);
      if (!media.length) return undefined;
      const currentIndex = media.indexOf(this.getCurrentMedia());
      const nextIndex = currentIndex < 0 ? 0 : (currentIndex + 1) % media.length;
      return media[nextIndex]?.getAttribute('data-media-id');
    };

    prototype.getPreviousMediaId = function getPreviousVisibleMediaId() {
      const media = visibleMedia(this);
      if (!media.length) return undefined;
      const currentIndex = media.indexOf(this.getCurrentMedia());
      const previousIndex = currentIndex <= 0 ? media.length - 1 : currentIndex - 1;
      return media[previousIndex]?.getAttribute('data-media-id');
    };

    prototype.zyvoriaFinishNavigation = true;
  };

  const patchProductInfo = () => {
    const ProductInfo = customElements.get('product-info');
    const prototype = ProductInfo?.prototype;
    if (
      !prototype
      || prototype.zyvoriaFinishMediaUpdate
      || typeof prototype.updateMedia !== 'function'
    ) {
      return;
    }

    const updateMedia = prototype.updateMedia;
    prototype.updateMedia = function updateTaggedFinishMedia(mediaId) {
      const selectedMedia = this.querySelector(`[data-image-id="${mediaId}"]`);
      const finish = selectedMedia?.dataset.zyvoriaFinish;
      if (finish && finish !== 'untagged') applyFinish(this, finish);
      return updateMedia.call(this, mediaId);
    };

    prototype.zyvoriaFinishMediaUpdate = true;
  };

  const patchZoomImages = () => {
    const ZoomImages = customElements.get('zoom-images');
    const prototype = ZoomImages?.prototype;
    if (
      !prototype
      || prototype.zyvoriaFinishNavigation
      || typeof prototype.createZoom !== 'function'
    ) {
      return;
    }

    const createZoom = prototype.createZoom;
    prototype.createZoom = function createFinishZoom(originalIndex) {
      if (!this.zyvoriaAllImages) {
        this.zyvoriaAllImages = [...this.querySelectorAll('[data-zoom-image]')];
      }

      const selectedImage = this.zyvoriaAllImages[originalIndex];
      const visibleImages = this.zyvoriaAllImages.filter(
        (image) => !image.closest('[data-image-id]')?.hidden,
      );
      this.images = visibleImages;
      const visibleIndex = Math.max(0, visibleImages.indexOf(selectedImage));
      return createZoom.call(this, visibleIndex);
    };

    prototype.zyvoriaFinishNavigation = true;
  };

  const setZoomMedia = (productInfo, matchingSlides) => {
    const zoom = productInfo.querySelector('zoom-images');
    if (!zoom) return;
    if (!zoom.zyvoriaAllImages) {
      zoom.zyvoriaAllImages = [...zoom.querySelectorAll('[data-zoom-image]')];
    }
    zoom.images = matchingSlides
      .map((slide) => slide.querySelector('[data-zoom-image]'))
      .filter(Boolean);
  };

  const applyFinish = (productInfo, finishValue) => {
    const finish = canonicalFinish(finishValue);
    if (!finish) return;

    const gallery = productInfo.querySelector('product-images');
    if (!gallery) return;

    const slides = [...gallery.querySelectorAll('[data-image-id][data-zyvoria-finish]')];
    const matchingSlides = slides.filter((slide) => slide.dataset.zyvoriaFinish === finish);
    const shownSlides = matchingSlides.length ? matchingSlides : slides;
    if (!matchingSlides.length) {
      console.warn(`Zyvora finish gallery: no media assigned to "${finish}".`);
    }

    slides.forEach((slide) => {
      const matches = !matchingSlides.length || slide.dataset.zyvoriaFinish === finish;
      slide.hidden = !matches;
      slide.toggleAttribute('inert', !matches);
      slide.setAttribute('aria-hidden', matches ? 'false' : 'true');
    });

    productInfo.querySelectorAll('[data-thumb-item][data-zyvoria-finish]').forEach((thumb) => {
      const matches = !matchingSlides.length || thumb.dataset.zyvoriaFinish === finish;
      thumb.hidden = !matches;
      thumb.toggleAttribute('inert', !matches);
      thumb.setAttribute('aria-hidden', matches ? 'false' : 'true');
    });

    productInfo.dataset.zyvoriaSelectedFinish = finish;
    gallery.dataset.zyvoriaSelectedFinish = finish;
    setZoomMedia(productInfo, shownSlides);

    const active = gallery.querySelector('[data-image-id].media--active:not([hidden])');
    const target = active || shownSlides[0];
    const mediaId = target?.getAttribute('data-media-id');
    if (mediaId) {
      gallery.dispatchEvent(new CustomEvent('theme:media:select', {
        bubbles: true,
        detail: {id: mediaId},
      }));
    }

    const rail = gallery.querySelector('[data-product-media-list]');
    if (rail) {
      rail.scrollLeft = 0;
      rail.style.columns = shownSlides.length === 1 ? '1' : '';
    }
    updateGalleryLayout(gallery, shownSlides.length);
    requestAnimationFrame(() => gallery.setHeight?.());
  };

  const selectedFinish = (productInfo) => {
    const inputs = [...productInfo.querySelectorAll('variant-selects input:checked')];
    const finishInput = inputs.find((input) => finishNames.has(optionName(input)));
    return finishInput?.value || productInfo.dataset.zyvoriaSelectedFinish;
  };

  const initializeProduct = (productInfo) => {
    if (productInfo.dataset.zyvoriaFinishReady === 'true') return;
    productInfo.dataset.zyvoriaFinishReady = 'true';

    productInfo.addEventListener('change', (event) => {
      const input = event.target;
      if (!finishNames.has(optionName(input))) return;
      applyFinish(productInfo, input.value);
    });

    applyFinish(productInfo, selectedFinish(productInfo));
  };

  const initialize = (root = document) => {
    root.querySelectorAll(productSelector).forEach(initializeProduct);
  };

  customElements.whenDefined('product-images').then(() => {
    patchProductImages();
    initialize();
  });

  customElements.whenDefined('product-info').then(patchProductInfo);

  if (customElements.get('zoom-images')) {
    patchZoomImages();
  } else {
    customElements.whenDefined('zoom-images').then(patchZoomImages);
  }

  document.addEventListener('shopify:section:load', (event) => initialize(event.target));
})();
